import { JSX, useEffect } from "react";
import { useAuth } from "../supabase/AuthProvider";
import { useLocation } from "react-router-dom";

const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";
const APP_AUTO_HANDOFF_URL = `${APP_HOME_URL}/auto-handoff`;
// Fallback: if auto-handoff route doesn't exist, /login still works (backward compatible)
const APP_LOGIN_URL = `${APP_HOME_URL}/login`;

const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();

  const { user, loading, sessionValidation } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      const returnTo = `${window.location.origin}${location.pathname}${location.search}`;
      // G2 terminal auth: the backend actively rejected the stored session
      // (revoked / blocked / access_locked) and the AuthProvider cleared it.
      // Auto-handoff cannot recover a rejected account and risks a redirect
      // loop, so go straight to the app-web login surface.
      if (sessionValidation === 'terminated') {
        window.location.href = `${APP_LOGIN_URL}?return_to=${encodeURIComponent(returnTo)}`;
        return;
      }
      // Check for circuit breaker: if we already attempted auto-handoff and still
      // have no session, fall back to /login to avoid an infinite redirect loop.
      const params = new URLSearchParams(window.location.search);
      if (params.get('handoff_attempted') === '1') {
        window.location.href = `${APP_LOGIN_URL}?return_to=${encodeURIComponent(returnTo)}`;
      } else {
        window.location.href = `${APP_AUTO_HANDOFF_URL}?return_to=${encodeURIComponent(returnTo)}`;
      }
    }
  }, [user, loading, sessionValidation, location.pathname, location.search]);

  // G2: while the backend has not yet confirmed a present session, do not
  // render any authenticated UI (loading state while validation is pending).
  if (loading || (user && sessionValidation === 'pending')) {
    return <>Loading...</>;
  }

  return user && sessionValidation !== 'terminated' ? children : null;
};

export default ProtectedRoute;
